// client/src/features/rent/periods.ts — the board's month arithmetic · RENT-003 (04 §8.5).
//
// A rent period is `(periodYear, periodMonth)` — part of the natural key 03 §8.6 makes unique
// per contract — and NOT a date. Keeping it as a pair rather than as a `Date` is what stops the
// month navigator from acquiring a timezone: `new Date(2026, 7, 1)` is midnight local, and the
// one thing this app has agreed never to do is let a business period shift across midnight
// (§9.9, D8, and the reason `formatDate` slices strings).
//
// ── Why the month names are twelve closures ─────────────────────────────────────────────
// `t(`rent.months.${month}`)` is exactly the dynamic key 04 §9.6.3 forbids and
// `npm run i18n:check` fails: i18next-parser cannot see it, so a translator never receives the
// string and the gate cannot tell a real key from a typo. §9.6.3 sanctions ONE computed shape,
// `enums.{EnumName}.{VALUE}`, and a month number is not an enum member. So the labels are
// twelve literal call sites behind closures — the pattern `nav-items.ts` uses and documents.

import type { TFunction } from 'i18next';

export interface Period {
  year: number;
  month: number;
}

/** 03 §8.6 — `CHECK (period_year BETWEEN 2000 AND 2100)`. Mirrored so the URL cannot exceed it. */
export const MIN_PERIOD_YEAR = 2000;
export const MAX_PERIOD_YEAR = 2100;

/**
 * The twelve month names, as literal `t()` calls. Index 0 is January, so `month - 1` indexes it.
 *
 * `Intl.DateTimeFormat('ro-RO', { month: 'long' })` would produce these without a translation
 * file, and is the wrong tool twice: the strings would bypass `romanian.json` (so §9.6's "every
 * user-visible string lives in one file" stops being true, and I18N-002's audit cannot see
 * them), and it would need a `Date` — the exact thing this module exists to avoid.
 */
const MONTH_LABELS: readonly ((t: TFunction) => string)[] = [
  (t) => t('rent.months.january'),
  (t) => t('rent.months.february'),
  (t) => t('rent.months.march'),
  (t) => t('rent.months.april'),
  (t) => t('rent.months.may'),
  (t) => t('rent.months.june'),
  (t) => t('rent.months.july'),
  (t) => t('rent.months.august'),
  (t) => t('rent.months.september'),
  (t) => t('rent.months.october'),
  (t) => t('rent.months.november'),
  (t) => t('rent.months.december'),
];

/** „August 2026". The period label of 04 §6.5's board row and of the month navigator. */
export function formatPeriod(period: Period, t: TFunction): string {
  const label = MONTH_LABELS[period.month - 1];
  if (label === undefined) return `${period.month}.${period.year}`;
  return `${label(t)} ${period.year}`;
}

/** Just the month name — the totals footer already carries the year in its heading. */
export function formatMonth(month: number, t: TFunction): string {
  const label = MONTH_LABELS[month - 1];
  return label === undefined ? String(month) : label(t);
}

/**
 * The period the board opens on when the URL names none.
 *
 * `now` is a parameter rather than a `new Date()` inside, so the board is testable without
 * faking the clock and so the caller decides what "today" means. Nothing here reads a timezone:
 * `getFullYear`/`getMonth` are local-time by definition, which for "which month is the landlord
 * looking at" is the right reading — a Bucharest landlord at 23:00 on the 31st means this month.
 */
export function currentPeriod(now: Date): Period {
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Move `delta` months, carrying the year. `shiftPeriod({2026, 12}, 1)` is `{2027, 1}`.
 *
 * Clamped to the DDL's year range so the navigator cannot walk the URL into a value the server
 * would reject with a 422 — the arrows disable at the ends instead (see `canShift`).
 */
export function shiftPeriod(period: Period, delta: number): Period {
  const zeroBased = period.year * 12 + (period.month - 1) + delta;
  const year = Math.floor(zeroBased / 12);
  const month = (zeroBased % 12) + 1;
  if (year < MIN_PERIOD_YEAR) return { year: MIN_PERIOD_YEAR, month: 1 };
  if (year > MAX_PERIOD_YEAR) return { year: MAX_PERIOD_YEAR, month: 12 };
  return { year, month };
}

/** Whether the navigator's arrow has anywhere to go — disabled, not hidden (§4.7). */
export function canShift(period: Period, delta: number): boolean {
  const next = shiftPeriod(period, delta);
  return next.year !== period.year || next.month !== period.month;
}
